import { ANIMATIONS } from "@/shared/data/animations";
import { POWERUP_LIST } from "@/shared/data/powerups";
import { computeDerivedStats } from "@/shared/utils/powerups";
import { useGameStore } from "@/shared/state/gameStore";
import { UnlockableAnimation } from "@/shared/types/unlock";
import { useGameRuntime } from "../context/GameContext";
import { Clapperboard, RotateCw, Sparkles } from "lucide-react";
import { ComponentType, useMemo } from "react";

const ANIMATION_BUTTON_ICON: Record<
  UnlockableAnimation,
  ComponentType<{ className?: string }>
> = {
  Backflip: RotateCw,
  Pose_confident: Sparkles,
  Dance: Clapperboard,
  AuraFarmer: Sparkles,
};

const formatCooldown = (seconds: number) => {
  if (seconds <= 0) return "";
  if (seconds < 10) return seconds.toFixed(1);
  return Math.ceil(seconds).toString();
};

const AnimationPowerButtons = () => {
  const runtime = useGameRuntime();
  const state = useGameStore();
  const derived = useMemo(() => computeDerivedStats(state, POWERUP_LIST), [state]);

  const unlockedAnimations = (
    Object.keys(ANIMATIONS) as UnlockableAnimation[]
  ).filter((animationId) => derived.unlockedAnimations[animationId] ?? false);

  if (unlockedAnimations.length === 0) return null;

  return (
    <>
      {unlockedAnimations.map((animationId, index) => {
        const definition = ANIMATIONS[animationId];
        const Icon = ANIMATION_BUTTON_ICON[animationId] ?? Sparkles;
        const cooldown = state.cooldowns.animations[animationId] ?? 0;
        const animationInProgress = state.activeAnimation != null;
        const disabled = cooldown > 0 || animationInProgress;
        const sideClass =
          index % 2 === 0
            ? "left-3 sm:left-4"
            : "right-3 sm:right-4";
        const topOffset = 128 + Math.floor(index / 2) * 86;

        return (
          <button
            key={animationId}
            type="button"
            onClick={() => runtime.activateAnimation(animationId)}
            disabled={disabled}
            className={[
              "pointer-events-auto absolute z-20 flex h-[4.5rem] w-[4.5rem] flex-col items-center justify-center rounded-[1.4rem] border border-fuchsia-200/12 bg-black/28 p-2 text-white shadow-[0_14px_36px_rgba(0,0,0,0.25)] backdrop-blur-xl transition sm:h-20 sm:w-20",
              disabled
                ? "cursor-not-allowed opacity-70"
                : "hover:border-fuchsia-300/36 hover:bg-black/40",
              sideClass,
            ].join(" ")}
            style={{ top: topOffset }}
          >
            <Icon className="h-5 w-5 text-fuchsia-200 sm:h-6 sm:w-6" />
            <span className="mt-2 text-center text-[10px] font-semibold leading-3 text-white/85">
              {definition.name}
            </span>

            {disabled ? (
              <div className="absolute inset-0 flex items-center justify-center rounded-[1.4rem] bg-black/45">
                <span className="rounded-full border border-fuchsia-200/15 bg-black/55 px-2 py-1 text-xs font-semibold text-white">
                  {animationInProgress ? "..." : formatCooldown(cooldown)}
                </span>
              </div>
            ) : null}
          </button>
        );
      })}
    </>
  );
};

export default AnimationPowerButtons;
